<template>
    <PlanbordGridCell
        v-if="'rankInRoster' in resource"
        :draggable="allowedToReorderUsers"
        @dragstart="handleDragDropEvents"
        @dragend="handleDragDropEvents"
        @dragover.prevent.stop="handleDragDropEvents"
        @dragleave.prevent="handleDragDropEvents"
        @drop.prevent.stop="handleDragDropEvents"
        class="name"
        :class="{'expanded': expanded, 'related': externalRoster}"
        @mouseenter="enableTooltip"
        @mouseleave="disableTooltip"
        info
        centeredVertically
    >
        <template v-if="showExpandButton">
            <span v-if="expanded" class="expand-button icon minus-boxed" @click="$emit('toggle:expanded', false)" />
            <span v-else class="expand-button icon plus-boxed" @click="$emit('toggle:expanded', true)" />
        </template>
        <NameMarker :hasAtwIssues="resource.atwIssues && resource.atwIssues.length > 0" :isFromRosterAssignment="isFromRosterAssignment">
            <span class="clickableName" @click="openModal">{{ resourceDisplayName }}</span>
        </NameMarker>
        <template v-if="externalRoster">
            <br /> <span class="external-roster-label">({{ externalRoster }})</span>
        </template>
    </PlanbordGridCell>
    <PlanbordGridCell
        v-else
        class="name"
        :class="{'expanded': expanded, 'related': externalRoster}"
        @mouseenter="enableTooltip"
        @mouseleave="disableTooltip"
        info
    >
        <NameMarker @click="openModal" :hasAtwIssues="resource.atwIssues && resource.atwIssues.length > 0" :isFromRosterAssignment="isFromRosterAssignment">
            <span class="clickableName" @click="openModal">{{ resourceDisplayName }}</span>
        </NameMarker>
        <template v-if="externalRoster">
            <br /> <span class="external-roster-label">({{ externalRoster }})</span>
        </template>
    </PlanbordGridCell>
</template>

<script lang="ts">
    import {Component, Vue, Prop} from 'vue-facing-decorator';
    import {MoveResourceRankData, MoveTitleRankData} from '@/types';
    import Tooltip from '@/components/Tooltip/Tooltip.vue';
    import ResourceDetailsModal from '@/components/modals/ResourceDetailsModal.vue';
    import {ResourceRosterRankUtil} from '@/util/ResourceRosterRankUtil';
    import {PlanbordResource, ResourceWithKeyedByDateObjects} from '@/types/api-object-extensions';
    import {PermissionsForRoster, RosterData} from '@/types/types';
    import {UserPermissions} from '@/types/enums/UserPermissions';
    import PlanbordGridCell from '@/components/Planbord/PlanbordGridCell.vue';
    import NameMarker from '@/components/Planbord/NameMarker.vue';
    import {ResourceUtil} from '@/util/ResourceUtil';
    import {ResourceRosterAssignment} from '@cquential/api';

    @Component({
        components: {
            PlanbordGridCell,
            NameMarker,
        },
        emits: [
            'move:resourceRosterRank',
            'move:rosterTitleRank',
            'toggle:expanded',
        ],
    })
    export default class ResourceNameCell extends Vue {
        @Prop({required: true}) public resource!: PlanbordResource | ResourceWithKeyedByDateObjects;
        @Prop({type: String}) public externalRoster?: string;
        @Prop() public permissions?: PermissionsForRoster;
        @Prop({type: Boolean}) public showExpandButton!: boolean;
        @Prop({type: Boolean}) public expanded!: boolean;
        @Prop({required: true}) public roster!: RosterData;

        public get allowedToReorderUsers(): boolean {
            return this.permissions?.[UserPermissions.ReorderUsers] || false;
        }

        public get firstViewedDay(): Date {
            return this.roster.days[0].date;
        }

        public get isFromRosterAssignment(): boolean {
            return this.resource.rosterAssignments?.some(
                (rosterAssignment: ResourceRosterAssignment): boolean => rosterAssignment.rosterId === this.roster.rosterId
                    && rosterAssignment.beginDate <= this.firstViewedDay
                    && (rosterAssignment.endDate === null || rosterAssignment.endDate >= this.firstViewedDay),
            ) ?? false;
        }

        public get resourceDisplayName(): string {
            return ResourceUtil.displayName(this.resource);
        }

        public enableTooltip(event: MouseEvent): void {
            /** @see Tooltip.setTooltip() */
            // @ts-expect-error TS2339
            Tooltip.__vfdConstructor.setTooltip({resource: this.resource!, event: event, type: 'atwIssues'});
        }

        public disableTooltip(): void {
            /** @see Tooltip.closeTooltip() */
            // @ts-expect-error TS2339
            Tooltip.__vfdConstructor.closeTooltip();
        }

        public openModal(): void {
            if (!this.resource) {
                console.error('Cannot open resource information modal if there is no resource.');

                return;
            }

            /** @see ResourceDetailsModal.open() */
            // @ts-expect-error TS2339
            ResourceDetailsModal.__vfdConstructor.open(this.resource, this.resource.employment!.user!.username);
        }

        public handleDragDropEvents(event: DragEvent): void {
            if (!this.allowedToReorderUsers) {
                return;
            }

            let target: HTMLDivElement | null = null;

            if (event.target) {
                let temptTarget: HTMLElement = event.target as HTMLElement;
                while (!temptTarget.classList.contains('planbord-grid-row') && temptTarget.parentElement) {
                    temptTarget = temptTarget.parentElement;
                }
                if (temptTarget.classList.contains('planbord-grid-row')) {
                    target = temptTarget as HTMLDivElement;
                }
            }

            if (target === null) {
                return;
            }

            switch (event.type) {
                case 'dragstart':
                    target.style.opacity = '0.5';
                    if (event.dataTransfer) {
                        const dataToTransfer: MoveResourceRankData = {
                            resourceId: Number(target.getAttribute('data-id')),
                            currentRank: Number(target.getAttribute('data-rank')),
                        };
                        event.dataTransfer.setData('text', JSON.stringify(dataToTransfer));
                    }
                    break;
                case 'dragend':
                    target.style.opacity = '';
                    break;
                case 'dragover':
                    target.style.opacity = '0.5';
                    break;
                case 'dragleave':
                    target.style.opacity = '';
                    break;
                case 'drop':
                    target.style.opacity = '1';
                    if (event.dataTransfer) {
                        let dataToTransfer: MoveTitleRankData | MoveResourceRankData;
                        try {
                            dataToTransfer = JSON.parse(event.dataTransfer.getData('text'));
                        } catch {
                            event.preventDefault();
                            break;
                        }
                        const newRank: number = Number(target.getAttribute('data-rank'));
                        if (dataToTransfer.currentRank !== newRank) {
                            dataToTransfer.newRank = newRank;
                            if (ResourceRosterRankUtil.isMoveResourceRankData(dataToTransfer)) {
                                this.$emit('move:resourceRosterRank', dataToTransfer);
                            } else if (ResourceRosterRankUtil.isMoveTitleRankData(dataToTransfer)) {
                                this.$emit('move:rosterTitleRank', dataToTransfer);
                            }
                        }
                    }
                    break;
            }
        }
    }
</script>

<style lang="scss" scoped>
    .name {
        --expand-button-size: 1.25rem;
        --expand-button-margin: 0.5rem;

        span.clickableName {
            cursor: pointer;

            &:hover {
                text-decoration: underline;
            }
        }

        // modifiers
        .external-roster-label {
            font-weight: 700;
            padding: 0.2rem;
            display: block;
        }

        .nameMarker:has(.expand-button) {
            display: grid;
            gap: var(--expand-button-margin);
            grid-template-columns: var(--expand-button-size) 1fr;
        }

        .expand-button {
            display: inline-block;
            vertical-align: middle;
            cursor: pointer;
            background: var(--header-text-color);
            margin-right: 0.5rem;
        }

        &.expanded.related {
            padding-left: calc(var(--td-padding) + var(--expand-button-size) + var(--expand-button-margin));
        }
    }
</style>
